import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { Snackbar, isMobileStyleMixin } from '@amzn/storm-ui';
import AACChromeAPI from '@amzn/aac-chrome-api';
import didElementLoad from 'src/commons/utils/didElementLoad';
import { getCloseEventHandler } from 'src/commons/utils';
import { logger } from 'src/commons/utils/logger';
import { BannerItem, BANNER_TYPE } from 'src/app/slice';

export const evenHandler = (
  props: BannerItem & { aacEventId: string },
): ((event?: Event) => void) =>
  getCloseEventHandler(props, AACChromeAPI.getCommonEventTarget());

const SnackbarContainerWrapper = styled.div`
  position: fixed;
  width: 450px;
  z-index: 500;
  left: unset;
  right: 22px;
  bottom: 22px;
  color: ${({ theme }) => theme.color.gray[1000]};
  ${isMobileStyleMixin(css`
    left: 0;
    right: 0;
    bottom: 18px;
    margin-right: auto;
    margin-left: auto;
    width: calc(100% - 28px);
  `)};
`;

export const SnackbarContainer: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const portalRef = document.getElementById('AACChromePortal') as Element;

  if (!portalRef) {
    return null;
  }

  return createPortal(
    <SnackbarContainerWrapper>{children}</SnackbarContainerWrapper>,
    portalRef,
  );
};

export const ToastBanner: React.VFC<{
  banner: BannerItem;
  bannerClosed: any;
  autoClose?: boolean;
}> = ({ banner, bannerClosed, autoClose }) => {
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (banner.type === BANNER_TYPE.SUCCESS || autoClose) {
      timer = setTimeout(() => {
        const portalRef = document.getElementById('AACChromePortal') as Element;
        const bannerElement = document.getElementById(banner.id);

        if (bannerElement && portalRef.contains(bannerElement)) {
          bannerClosed(banner);
        }
      }, 4000);
    }
    return () => clearTimeout(timer);
  }, []);

  return (
    <Snackbar
      id={banner.id}
      variant={banner.type}
      onClose={
        banner.type === BANNER_TYPE.SUCCESS || banner.withCloseButton === false
          ? undefined
          : () => bannerClosed(banner)
      }
    >
      {banner.message}
    </Snackbar>
  );
};

const ToastBannerContainer: React.VFC<{
  toastBanner: BannerItem;
  isMobile: boolean;
}> = ({ toastBanner, isMobile }) => {
  const [banners, setBanners] = useState<BannerItem[]>([]);
  useEffect(() => {
    if (banners.some((banner) => banner.id === toastBanner.id)) {
      setBanners(
        banners.map((banner) =>
          banner.id === toastBanner.id ? toastBanner : banner,
        ),
      );
    } else setBanners(banners.concat(toastBanner));
  }, [toastBanner]);

  useEffect(() => {
    didElementLoad('toastbanner');
  }, []);

  const bannerClosed = (banner: BannerItem) => {
    try {
      const generatedEventHandler = evenHandler({
        ...banner,
        aacEventId: banner.id,
      });
      /*
       * `window.event` spelled out. This was previously the bare identifier
       * `event`, which resolves to the same deprecated global -- it is not a
       * parameter, since the Snackbar calls onClose with no arguments. It holds the
       * click currently dispatching, and is undefined on the auto-close timeout
       * path, which is harmless: originEvent simply goes unset.
       */
      generatedEventHandler(window.event);
    } catch (error: any) {
      logger.error(
        'A !success Snackbar component was called without providing an onClose callback',
        error,
      );
      logger.error(isMobile ? 'mobile-snackbar' : 'desktop-snackbar');
    }
    setBanners((banners) =>
      banners.filter((currentBanner) => currentBanner.id !== banner.id),
    );
  };

  if (banners.length === 0) return null;

  return (
    <SnackbarContainer>
      {banners.map((banner) => (
        <ToastBanner
          key={banner.id}
          banner={banner}
          bannerClosed={bannerClosed}
        />
      ))}
    </SnackbarContainer>
  );
};

export default ToastBannerContainer;
